// Modal "Investigar correos" del detalle de usuario.
//
// Corre la investigación de entregabilidad de email server-side (endpoint
// /admin/users/{id}/email-report, dos fases start+poll porque las queries de
// Logs Insights tardan 5-30 s) mostrando un spinner, y al terminar renderiza
// el reporte (veredicto + KPIs + timeline + infra) scrolleable en el modal.
//
// "Bajar como PDF": genera el archivo .pdf 100% client-side con pdfmake
// (lib/emailReportPdf.ts) y dispara la descarga directa — sin depender del
// diálogo de impresión del navegador (que en mobile no es confiable).
//
// Metodología: auditorías investigations/ 2026-07-10 / 2026-07-11 y
// scripts/investigations/email-delivery-report.mjs.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  Check,
  CheckCircle2,
  Download,
  KeyRound,
  LogIn,
  Mail,
  MailCheck,
  MailWarning,
  Send,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  UserCheck,
  UserPlus,
  UserX,
  X,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import {
  adminStartEmailReport,
  adminPollEmailReport,
  AdminApiError,
  type EmailReport,
  type EmailReportEvent,
} from '@/lib/api';
import { downloadEmailReportPdf } from '@/lib/emailReportPdf';

const DAYS = 7;
const POLL_MS = 2500;
const MAX_POLLS = 48; // ~2 min

// ---------------------------------------------------------------------------
// Formato
// ---------------------------------------------------------------------------

const ART_FMT = new Intl.DateTimeFormat('es-AR', {
  timeZone: 'America/Argentina/Buenos_Aires',
  dateStyle: 'short',
  timeStyle: 'medium',
  hour12: false,
});
const ART_TIME_FMT = new Intl.DateTimeFormat('es-AR', {
  timeZone: 'America/Argentina/Buenos_Aires',
  timeStyle: 'medium',
  hour12: false,
});

const ART_DATE_FMT = new Intl.DateTimeFormat('es-AR', {
  timeZone: 'America/Argentina/Buenos_Aires',
  dateStyle: 'short',
});

const fmtArt = (iso: string | null | undefined): string => (iso ? ART_FMT.format(new Date(iso)) : '—');
const fmtArtTime = (iso: string): string => ART_TIME_FMT.format(new Date(iso));
const fmtArtDate = (iso: string): string => ART_DATE_FMT.format(new Date(iso));
const fmtDelta = (sec: number): string =>
  sec < 120 ? `+${sec}s` : sec < 7200 ? `+${Math.round(sec / 60)}min` : `+${(sec / 3600).toFixed(1)}h`;
const fmtNum = (n: number | null | undefined): string => (n == null ? '—' : n.toLocaleString('es-AR'));
const fmtPct = (f: number | null | undefined): string =>
  f == null ? '—' : `${(f * 100).toLocaleString('es-AR', { maximumFractionDigits: 2 })}%`;

// Ícono + chip tintado por tipo de evento del timeline. La FORMA del ícono
// distingue el evento (no solo el color — funciona en B&N y para daltónicos).
const EVENT_VISUAL: Record<string, { Icon: LucideIcon; chip: string; icon: string }> = {
  created: { Icon: UserPlus, chip: 'bg-ink-muted/10', icon: 'text-ink-subtle' },
  send_verif: { Icon: Mail, chip: 'bg-status-info/15', icon: 'text-status-info' },
  send_reset: { Icon: KeyRound, chip: 'bg-brand/15', icon: 'text-brand' },
  fail: { Icon: XCircle, chip: 'bg-status-error/15', icon: 'text-status-error' },
  verified: { Icon: MailCheck, chip: 'bg-status-success/15', icon: 'text-status-success' },
  reset_done: { Icon: CheckCircle2, chip: 'bg-status-success/15', icon: 'text-status-success' },
  login_reject: { Icon: ShieldAlert, chip: 'bg-status-warning/15', icon: 'text-status-warning' },
  login_ok: { Icon: LogIn, chip: 'bg-status-success/15', icon: 'text-status-success' },
};

const VERDICT_CLASS: Record<EmailReport['verdict']['status'], { border: string; bg: string; Icon: LucideIcon }> = {
  good: { border: 'border-status-success', bg: 'bg-status-success/10', Icon: CheckCircle2 },
  warning: { border: 'border-status-warning', bg: 'bg-status-warning/10', Icon: AlertTriangle },
  serious: { border: 'border-[#ea580c]', bg: 'bg-[#ea580c]/10', Icon: AlertTriangle },
  critical: { border: 'border-status-error', bg: 'bg-status-error/10', Icon: XCircle },
};

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

type Phase =
  | { name: 'running'; startedAt: number; email?: string | null }
  | { name: 'done'; report: EmailReport }
  | { name: 'error'; message: string };

export default function EmailReportModal({
  userId,
  open,
  onClose,
}: {
  userId: string;
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const ref = useRef<HTMLDialogElement>(null);
  const [phase, setPhase] = useState<Phase>({ name: 'running', startedAt: Date.now() });
  const [elapsed, setElapsed] = useState(0);
  const [pdfBusy, setPdfBusy] = useState(false);

  async function onDownloadPdf(report: EmailReport): Promise<void> {
    setPdfBusy(true);
    try {
      await downloadEmailReportPdf(report);
    } finally {
      setPdfBusy(false);
    }
  }

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
  }, [open]);

  // Investigación: start + poll hasta done (cancelable al cerrar).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPhase({ name: 'running', startedAt: Date.now() });

    (async () => {
      try {
        const started = await adminStartEmailReport(userId, DAYS);
        // La dirección investigada se conoce ni bien arranca — mostrarla ya.
        if (!cancelled) setPhase((p) => (p.name === 'running' ? { ...p, email: started.email } : p));
        for (let i = 0; i < MAX_POLLS && !cancelled; i++) {
          await new Promise((r) => setTimeout(r, POLL_MS));
          if (cancelled) return;
          const res = await adminPollEmailReport(userId, started.query_ids, DAYS);
          if (res.phase === 'done') {
            if (!cancelled) setPhase({ name: 'done', report: res.report });
            return;
          }
        }
        if (!cancelled) setPhase({ name: 'error', message: 'La investigación tardó demasiado. Reintentá en unos minutos.' });
      } catch (e) {
        if (!cancelled) {
          setPhase({
            name: 'error',
            message: e instanceof AdminApiError ? `${e.code}: ${e.message}` : 'Error inesperado consultando el backend.',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  // Cronómetro del spinner.
  useEffect(() => {
    if (!open || phase.name !== 'running') return;
    const t = setInterval(() => setElapsed(Math.round((Date.now() - phase.startedAt) / 1000)), 1000);
    return () => clearInterval(t);
  }, [open, phase]);

  if (!open) return null;

  return (
    <dialog
      ref={ref}
      aria-labelledby="email-report-title"
      onClose={onClose}
      className="rounded-xl p-0 w-full max-w-4xl bg-surface dark:bg-surface-dark text-ink dark:text-ink-inverted backdrop:bg-black/50"
    >
      <div className="flex flex-col max-h-[85vh]">
        <header className="flex items-center gap-3 px-6 py-4 border-b border-surface-border dark:border-surface-dark-border">
          <h2 id="email-report-title" className="flex-1 min-w-0 leading-tight">
            <span className="block text-xs font-medium text-ink-muted dark:text-ink-inverted-muted">
              Investigación sobre entregabilidad de E-Mail
            </span>
            <span className="block text-lg font-semibold font-mono break-all min-h-[1.75rem]">
              {phase.name === 'done' ? phase.report.email ?? '(sin email)' : phase.name === 'running' ? phase.email ?? '…' : '…'}
            </span>
          </h2>
          {phase.name === 'done' ? (
            <button
              type="button"
              disabled={pdfBusy}
              onClick={() => void onDownloadPdf(phase.report)}
              className="min-h-[2.25rem] inline-flex items-center gap-1.5 rounded-lg bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 focus-visible:ring-2 focus-visible:ring-brand-light disabled:opacity-60 disabled:cursor-wait"
            >
              <Download size={15} aria-hidden="true" />
              {pdfBusy ? 'Generando…' : 'Bajar como PDF'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="min-h-[2.25rem] min-w-[2.25rem] inline-flex items-center justify-center rounded-lg border border-surface-border dark:border-surface-dark-border hover:bg-surface-muted dark:hover:bg-surface-dark-muted"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <div className="overflow-y-auto px-6 py-5">
          {phase.name === 'running' ? (
            <div className="flex flex-col items-center gap-4 py-16" role="status" aria-live="polite">
              <div className="h-12 w-12 rounded-full border-4 border-brand/25 border-t-brand animate-spin" aria-hidden="true" />
              <p className="font-medium">Investigación en curso…</p>
              <p className="text-sm text-ink-muted dark:text-ink-inverted-muted text-center max-w-md">
                Consultando Cognito, SES (suppression, cuota, identidad del dominio) y los logs de envío,
                verificación y login de los últimos {DAYS} días.
              </p>
              <p className="text-xs text-ink-subtle tabular-nums">{elapsed}s</p>
            </div>
          ) : phase.name === 'error' ? (
            <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-4 text-sm text-status-error my-8">
              {phase.message}
            </div>
          ) : (
            <ReportBody report={phase.report} />
          )}
        </div>
      </div>
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// Cuerpo del reporte (estilos del backoffice)
// ---------------------------------------------------------------------------

function TimelineRow({ e }: { e: EmailReportEvent }): JSX.Element {
  const range = e.count > 1 ? `${fmtArt(e.ts)} → ${fmtArtTime(e.last_ts)}` : fmtArt(e.ts);
  const vis = EVENT_VISUAL[e.kind] ?? EVENT_VISUAL['created']!;
  return (
    <li className="py-1 flex gap-2.5 items-start">
      <span className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center ${vis.chip}`} aria-hidden="true">
        <vis.Icon size={13} className={vis.icon} />
      </span>
      <span className="text-xs text-ink-subtle tabular-nums whitespace-nowrap min-w-[10.5rem] pt-1">{range}</span>
      <span className="text-sm pt-0.5">
        {e.label}
        {e.count > 1 ? <span className="text-ink-muted dark:text-ink-inverted-muted"> ×{e.count}</span> : null}
        {e.message_id ? (
          <span className="font-mono text-xs text-ink-subtle break-all" title={e.message_id}>
            {' '}{e.message_id.slice(0, 18)}…
          </span>
        ) : null}
        {e.used_after_sec != null ? (
          <b className="text-status-success"> · usado {fmtDelta(e.used_after_sec)}</b>
        ) : null}
        {e.invalidated ? <b className="text-status-warning"> · link invalidado por reenvío posterior</b> : null}
      </span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Tiles KPI — lenguaje semáforo, legible de un golpe de vista.
// El estado lo dice la FORMA del ícono + el color + la palabra; el caso
// crítico además enciende el tile entero (borde + wash).
// ---------------------------------------------------------------------------

type TileStatus = 'good' | 'warning' | 'serious' | 'critical' | 'neutral';

const TILE_STATUS: Record<TileStatus, { chip: string; icon: string; tile: string; value: string }> = {
  good: { chip: 'bg-status-success/15', icon: 'text-status-success', tile: '', value: '' },
  warning: { chip: 'bg-status-warning/15', icon: 'text-status-warning', tile: 'border-status-warning/50', value: 'text-status-warning' },
  serious: { chip: 'bg-[#ea580c]/15', icon: 'text-[#ea580c]', tile: 'border-[#ea580c]/60 bg-[#ea580c]/5', value: 'text-[#ea580c]' },
  critical: { chip: 'bg-status-error/15', icon: 'text-status-error', tile: 'border-status-error/70 bg-status-error/5', value: 'text-status-error' },
  neutral: { chip: 'bg-ink-muted/10', icon: 'text-ink-subtle', tile: '', value: '' },
};

function Tile({ label, value, note, status, Icon }: {
  label: string;
  value: ReactNode;
  note: string;
  status: TileStatus;
  Icon: LucideIcon;
}): JSX.Element {
  const c = TILE_STATUS[status];
  return (
    <div className={`rounded-lg border border-surface-border dark:border-surface-dark-border px-3 py-2.5 flex items-center gap-2.5 ${c.tile}`}>
      <span className={`h-10 w-10 shrink-0 rounded-lg flex items-center justify-center ${c.chip}`}>
        <Icon size={22} className={c.icon} aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] text-ink-subtle leading-tight">{label}</div>
        <div className={`text-lg font-semibold leading-tight ${c.value}`}>{value}</div>
        <div className="text-[11px] text-ink-muted dark:text-ink-inverted-muted leading-tight">{note}</div>
      </div>
    </div>
  );
}

function Badge({ ok, children }: { ok: boolean; children: string }): JSX.Element {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs mr-1.5 mb-1.5 ${
        ok
          ? 'border-status-success/40 text-ink-muted dark:text-ink-inverted-muted'
          : 'border-status-error text-status-error font-semibold'
      }`}
    >
      {ok ? (
        <Check size={11} className="text-status-success shrink-0" aria-hidden="true" />
      ) : (
        <X size={11} className="shrink-0" aria-hidden="true" />
      )}
      {children}
    </span>
  );
}

// Exportado para el harness de verificación visual (render estático en Node).
export function ReportBody({ report }: { report: EmailReport }): JSX.Element {
  const v = report.verdict;
  const vc = VERDICT_CLASS[v.status];
  const k = report.kpis;

  return (
    <div className="space-y-5">
      <div className={`rounded-lg border-l-4 ${vc.border} ${vc.bg} px-4 py-3`}>
        <p className="font-semibold text-[15px] flex items-start gap-1.5">
          <vc.Icon size={17} className="shrink-0 mt-0.5" aria-hidden="true" />
          {v.title}
        </p>
        <p className="text-sm text-ink-muted dark:text-ink-inverted-muted mt-1">{v.detail}</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
        <Tile
          label="Mails enviados"
          value={k.mails_sent}
          note={`${k.mails_verification} verif · ${k.mails_reset} reset (${report.days}d)`}
          status={k.mails_sent > 0 ? 'good' : 'neutral'}
          Icon={Send}
        />
        <Tile
          label="Fallos de envío"
          value={k.send_failures}
          note={
            k.send_failures === 0
              ? 'SES aceptó todo'
              : k.mails_sent === 0
                ? 'TODOS los envíos fallaron'
                : `fallos parciales (${k.send_failures} de ${k.mails_sent + k.send_failures})`
          }
          status={k.send_failures === 0 ? 'good' : k.mails_sent === 0 ? 'critical' : 'serious'}
          Icon={k.send_failures === 0 ? CheckCircle2 : k.mails_sent === 0 ? XCircle : AlertTriangle}
        />
        <Tile
          label="Email verificado"
          value={k.email_verified_db ? 'Sí' : 'NO'}
          note={k.verified_at ? `link usado ${fmtArt(k.verified_at)}` : k.email_verified_db ? 'verificado (histórico)' : 'el usuario no puede entrar'}
          status={k.email_verified_db ? 'good' : 'warning'}
          Icon={k.email_verified_db ? MailCheck : MailWarning}
        />
        <Tile
          label="Casilla destino"
          value={k.suppressed_reason ? 'Bloqueada' : 'Limpia'}
          note={k.suppressed_reason ? `suprimida por ${k.suppressed_reason}` : 'sin bloqueos de SES'}
          status={k.suppressed_reason ? 'critical' : 'good'}
          Icon={k.suppressed_reason ? ShieldX : ShieldCheck}
        />
        <Tile
          label="Cuenta"
          value={report.identification.account_status === 'active' ? 'Activa' : report.identification.account_status}
          note={`alta ${fmtArt(report.identification.created)}`}
          status={report.identification.account_status === 'active' ? 'good' : 'critical'}
          Icon={report.identification.account_status === 'active' ? UserCheck : UserX}
        />
      </div>

      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted dark:text-ink-inverted-muted mb-2">
          Timeline ({report.timeline.length} eventos, hora ART)
        </h3>
        {report.timeline.length ? (
          <ol>
            {report.timeline.map((e, i) => (
              <TimelineRow key={`${e.ts}-${i}`} e={e} />
            ))}
          </ol>
        ) : (
          <p className="text-sm text-ink-muted dark:text-ink-inverted-muted">Sin eventos para esta cuenta en la ventana.</p>
        )}
      </section>

      <div className="grid md:grid-cols-2 gap-6">
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted dark:text-ink-inverted-muted mb-2">Identificación</h3>
          <dl className="text-sm space-y-1.5">
            <Row k="Cognito sub" v={report.identification.cognito_sub} mono />
            <Row k="user_id" v={report.identification.user_id} mono />
            <Row k="Estado" v={`${report.identification.account_status}${report.identification.cognito_status ? ` · Cognito ${report.identification.cognito_status}` : ''}`} />
            <Row k="Alta" v={fmtArt(report.identification.created)} />
            <Row k="Últ. modificación" v={fmtArt(report.identification.modified)} />
          </dl>

          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted dark:text-ink-inverted-muted mt-4 mb-2">Cuentas similares</h3>
          {report.twins.length ? (
            <>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-border dark:border-surface-dark-border text-ink-subtle">
                    <th className="text-left text-[11px] font-medium py-1.5 pr-2">Dirección</th>
                    <th className="w-9 py-1.5" title="Cuenta confirmada">
                      <UserCheck size={14} className="mx-auto" aria-hidden="true" />
                      <span className="sr-only">Confirmada</span>
                    </th>
                    <th className="w-20 py-1.5" title="Fecha de alta">
                      <CalendarClock size={14} className="mx-auto" aria-hidden="true" />
                      <span className="sr-only">Fecha de alta</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.twins.slice(0, 8).map((t) => (
                    <tr key={t.email} className="border-b border-surface-border/60 dark:border-surface-dark-border/60">
                      <td className="py-1.5 pr-2">
                        <code className="font-mono text-xs break-all">{t.email}</code>
                      </td>
                      <td className="text-center">
                        {t.status === 'CONFIRMED' ? (
                          <Check size={14} className="mx-auto text-status-success" aria-label="confirmada" />
                        ) : (
                          <X size={14} className="mx-auto text-status-error" aria-label={t.status.toLowerCase()} />
                        )}
                      </td>
                      <td className="text-right tabular-nums leading-tight py-1">
                        {t.created ? (
                          <>
                            <div className="text-xs">{fmtArtDate(t.created)}</div>
                            <div className="text-[10px] text-ink-subtle">{fmtArtTime(t.created)}</div>
                          </>
                        ) : (
                          <span className="text-xs text-ink-subtle">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {report.twins_masked ? (
                <p className="text-[11px] text-ink-subtle mt-1.5">
                  Emails de terceros enmascarados — el rol administrador los ve completos.
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-ink-muted dark:text-ink-inverted-muted">Sin cuentas con prefijo similar.</p>
          )}
        </section>

        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted dark:text-ink-inverted-muted mb-2">Infraestructura de correo</h3>
          <div>
            <Badge ok={report.infra.sending_enabled}>SES sending</Badge>
            <Badge ok={report.infra.enforcement === 'HEALTHY'}>{`enforcement ${report.infra.enforcement}`}</Badge>
            <Badge ok={report.infra.dkim === 'SUCCESS'}>{`DKIM ${report.infra.dkim ?? '?'}${report.infra.domain ? ` (${report.infra.domain})` : ''}`}</Badge>
            <Badge ok={report.infra.spf}>SPF</Badge>
            <Badge ok={!!report.infra.dmarc_policy}>{`DMARC ${report.infra.dmarc_policy ?? 'sin registro'}`}</Badge>
            <Badge ok={report.infra.quota_sent_24h < report.infra.quota_max_24h * 0.9}>
              {`quota ${fmtNum(report.infra.quota_sent_24h)} / ${fmtNum(report.infra.quota_max_24h)}`}
            </Badge>
            <Badge ok>{`rate máx ${report.infra.max_send_rate}/s`}</Badge>
          </div>

          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted dark:text-ink-inverted-muted mt-4 mb-2">Plataforma — últimas 24 h</h3>
          <PlatformSection report={report} />
        </section>
      </div>

      <p className="text-[11px] text-ink-subtle border-t border-surface-border dark:border-surface-dark-border pt-3">
        Generado {fmtArt(report.generated_at)} ART · ventana {report.days} días · lecturas 100% read-only ·
        uso interno — contiene datos del usuario del reclamo. Metodología: auditorías investigations/ 2026-07.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plataforma — contadores + medidores con umbral marcado.
// El track del medidor llega hasta 1,25× el umbral: la marca queda al 80% y
// se VE cuánto margen queda antes del límite.
// ---------------------------------------------------------------------------

type MeterZone = 'none' | 'good' | 'warning' | 'critical';

const METER_FILL: Record<MeterZone, string> = {
  none: 'bg-ink-muted/20',
  good: 'bg-status-success',
  warning: 'bg-status-warning',
  critical: 'bg-status-error',
};
const METER_TEXT: Record<MeterZone, string> = {
  none: 'text-ink-subtle',
  good: 'text-ink dark:text-ink-inverted',
  warning: 'text-status-warning',
  critical: 'text-status-error',
};

function meterZone(value: number | null, warnAt: number, badAt: number): MeterZone {
  if (value == null) return 'none';
  if (value >= badAt) return 'critical';
  if (value >= warnAt) return 'warning';
  return 'good';
}

function Meter({ label, valueLabel, fillPct, tickPct, zone }: {
  label: string;
  valueLabel: string;
  fillPct: number;
  tickPct: number;
  zone: MeterZone;
}): JSX.Element {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] text-ink-muted dark:text-ink-inverted-muted">{label}</span>
        <span className={`text-xs font-semibold tabular-nums ${METER_TEXT[zone]}`}>{valueLabel}</span>
      </div>
      <div className="relative h-1.5 mt-1 rounded-full bg-ink-muted/10 overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 rounded-full ${METER_FILL[zone]}`}
          style={{ width: `${Math.max(zone === 'none' ? 0 : 2, Math.min(100, fillPct))}%` }}
        />
        <div className="absolute inset-y-0 w-px bg-ink-muted/60" style={{ left: `${tickPct}%` }} title="umbral" />
      </div>
    </div>
  );
}

function MiniStat({ label, value, bad }: { label: string; value: string; bad?: boolean }): JSX.Element {
  return (
    <div className="rounded-lg bg-surface-muted dark:bg-surface-dark-muted px-2.5 py-2">
      <div className={`text-base font-semibold tabular-nums leading-tight ${bad ? 'text-status-error' : ''}`}>{value}</div>
      <div className="text-[10px] text-ink-muted dark:text-ink-inverted-muted leading-tight">{label}</div>
    </div>
  );
}

function PlatformSection({ report }: { report: EmailReport }): JSX.Element {
  const p = report.platform_24h;
  const quotaPct = report.infra.quota_max_24h > 0 ? (report.infra.quota_sent_24h / report.infra.quota_max_24h) * 100 : null;
  const bounce = p.reputation_bounce_rate;
  const complaint = p.reputation_complaint_rate;
  return (
    <>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <MiniStat label="mails enviados" value={fmtNum(p.email_sent)} />
        <MiniStat label="fallos de envío" value={fmtNum(p.email_failed)} bad={(p.email_failed ?? 0) > 0} />
        <MiniStat label="throttles anti-abuse" value={fmtNum(p.abuse_throttled)} />
      </div>
      <div className="space-y-2.5">
        <Meter
          label="Reputación — rebotes (umbral 5%)"
          valueLabel={fmtPct(bounce)}
          fillPct={bounce == null ? 0 : (bounce / 0.0625) * 100}
          tickPct={80}
          zone={meterZone(bounce, 0.025, 0.05)}
        />
        <Meter
          label="Reputación — quejas (umbral 0,1%)"
          valueLabel={fmtPct(complaint)}
          fillPct={complaint == null ? 0 : (complaint / 0.00125) * 100}
          tickPct={80}
          zone={meterZone(complaint, 0.0005, 0.001)}
        />
        <Meter
          label="Cuota diaria de envío (límite 90%)"
          valueLabel={`${fmtNum(report.infra.quota_sent_24h)} / ${fmtNum(report.infra.quota_max_24h)}`}
          fillPct={quotaPct ?? 0}
          tickPct={90}
          zone={meterZone(quotaPct, 70, 90)}
        />
      </div>
      <p className="text-[11px] text-ink-subtle mt-2">
        Reputación: métricas nativas de AWS SES sobre la cuenta de envío (AWS no la desagrega por dominio sin VDM).
      </p>
    </>
  );
}

function Row({ k, v, mono, bad }: { k: string; v: string; mono?: boolean; bad?: boolean }): JSX.Element {
  return (
    <div className="flex gap-3">
      <dt className="text-ink-subtle min-w-[10rem]">{k}</dt>
      <dd className={`${mono ? 'font-mono text-xs pt-0.5' : ''} ${bad ? 'text-status-error font-semibold' : ''} break-all`}>{v}</dd>
    </div>
  );
}
